import BaseSource from './BaseSource'

import { FeatureFlagsSource, Flags } from '../model'

interface ConfigCatSourceProps {
  configCat: ConfigCat
  isClient: boolean
  options: ConfigCatInitialProps
}

interface ConfigCatInitialProps {
  SDKKey: string
  defaultFlags?: Flags
  pollIntervalSeconds?: number
}
export interface User {
  identifier: string
  email?: string
  country?: string
  custom?: { [key: string]: string }
}
interface CacheValue {
  key: string
  value: boolean | undefined
}

interface ConfigCat {
  createClientWithAutoPoll: (sdk: string, options: AutoPollOptions) => ConfigCatClient
  createClientWithManualPoll: (sdk: string) => ConfigCatClient
}

interface AutoPollOptions {
  pollIntervalSeconds: number
  configChanged: () => void
}
interface ConfigCatClient {
  getAllKeysAsync: () => Promise<string[]>
  getValueAsync: (key: string, defaultValue: boolean, user?: User) => Promise<boolean>
  forceRefreshAsync: () => Promise<void>
}

export default class ConfigCatSource extends BaseSource implements FeatureFlagsSource {
  configCat: ConfigCat
  isClient: boolean
  configCatClient: ConfigCatClient | undefined
  SDKKey: string
  defaultFlags: Flags
  pollIntervalSeconds: number
  flags: Flags
  user?: User

  constructor(props: ConfigCatSourceProps) {
    super()
    const { configCat, isClient, options } = props
    const { SDKKey, defaultFlags, pollIntervalSeconds } = options

    this.configCat = configCat
    this.isClient = isClient
    this.SDKKey = SDKKey
    this.defaultFlags = defaultFlags || {}
    this.pollIntervalSeconds = pollIntervalSeconds || 150
    this.flags = {}
  }

  init() {
    if (this.isClient) {
      this.initClient()
    } else {
      this.initSSR()
    }
  }

  initClient() {
    this.configCatClient = this.configCat.createClientWithAutoPoll(this.SDKKey, {
      pollIntervalSeconds: this.pollIntervalSeconds,
      configChanged: () => {
        this.updateSourceFlags()
      },
    })

    this.updateSourceFlags()
  }

  initSSR() {
    this.configCatClient = this.configCat.createClientWithManualPoll(this.SDKKey)
  }

  setUser(user?: User) {
    this.user = user

    this.updateSourceFlags()
  }

  async getCacheValues(): Promise<CacheValue[]> {
    if (!this.configCatClient) return []

    try {
      const keys: string[] = await this.configCatClient?.getAllKeysAsync()
      if (!keys) return []
      return Promise.all(
        keys.map(async (key) => {
          const value = await this.configCatClient?.getValueAsync(
            key,
            this.flags[key] || this.defaultFlags[key],
            this.user,
          )
          return { key, value }
        }),
      )
    } catch {
      return []
    }
  }

  async buildFlags(): Promise<Flags> {
    const cacheValues = await this.getCacheValues()

    if (!cacheValues?.length) return {}

    return cacheValues.reduce((flags: Flags, cacheValue: CacheValue) => {
      const { key, value } = cacheValue
      return { ...flags, [key]: !!value }
    }, {})
  }

  async updateSourceFlags() {
    this.flags = await this.buildFlags()
    this.update(this.flags)
  }

  async getFlags() {
    if (this.isClient) {
      return this.flags
    }

    if (!this.configCatClient) return {}

    try {
      await this.configCatClient.forceRefreshAsync()
      return await this.buildFlags()
    } catch {
      return {}
    }
  }
}
